import type { TableSpec, JoinSpec } from "../../lib/funnel/SchemaDiagram";

// Schema diagram config for DEMO_FUNNEL_SQL. Spine is reporting.demo
// (is_demo=true); attendees explode into per-attendee rows that join out to
// users, billing, activation, and the lead_cal booking enrichment.

export const DEMO_FUNNEL_TABLES: TableSpec[] = [
  // Top-left: spine
  { id: "demo", schema: "reporting", alias: "d", label: "demo", x: 60, y: 20, columns: [
    { name: "external_id" },
    { name: "created_date_utc" },
    { name: "organiser_email", note: "ILIKE '%@gomarloo.com'" },
    { name: "attendee_emails", note: "unnest → demo_attendees CTE" },
    { name: "follow_up_context", note: "is_demo=true; competitors, integrations, …" },
  ] },
  { id: "demos_cte", schema: "cte", alias: "dm", label: "demos", x: 60, y: 320, columns: [
    { name: "demo_external_id" },
    { name: "demo_date_utc" },
    { name: "lead_status / demo_type / urgency" },
    { name: "competitors_mentioned (jsonb)" },
    { name: "integration_requests (jsonb)" },
  ] },
  { id: "demo_att_cte", schema: "cte", alias: "da", label: "demo_attendees", x: 60, y: 600, columns: [
    { name: "demo_external_id" },
    { name: "attendee_email", note: "lower(unnest)" },
    { name: "@gomarloo.com excluded" },
  ] },
  { id: "first_demo_cte", schema: "cte", alias: "fd", label: "first_demo_per_attendee", x: 60, y: 850, columns: [
    { name: "attendee_email" },
    { name: "first_demo_date" },
  ] },

  // Booking-side
  { id: "lead_cal", schema: "reporting", alias: "lc", label: "lead_cal", x: 1100, y: 20, columns: [
    { name: "external_id" },
    { name: "client_email", note: "lower() ∈ attendee_emails" },
    { name: "created_date_utc", note: "±60d of demo" },
    { name: "utm_source / medium / campaign" },
    { name: "country_code / advisor_count" },
  ] },
  { id: "booking_match_cte", schema: "cte", alias: "bm", label: "booking_match", x: 1100, y: 320, columns: [
    { name: "demo_external_id" },
    { name: "lead_cal_external_id" },
    { name: "booking_channel_bucket", note: "Meta / Google / LinkedIn / …" },
  ] },

  // User-side
  { id: "users", schema: "public", alias: "u", label: "users", x: 600, y: 600, columns: [
    { name: "external_id" },
    { name: "email" },
    { name: "first_name / last_name" },
    { name: "created_at_utc", note: "= signup_date_utc" },
    { name: "attribution / attribution_category" },
  ] },
  { id: "user_roles", schema: "public", alias: "ur", label: "user_roles", x: 600, y: 880, columns: [
    { name: "user_external_id" },
    { name: "company_external_id" },
    { name: "role" },
    { name: "status", note: "prefer active" },
  ] },
  { id: "companies", schema: "public", alias: "c", label: "companies", x: 920, y: 880, columns: [
    { name: "external_id" },
    { name: "name" },
    { name: "country_code" },
  ] },
  { id: "attendee_user_cte", schema: "cte", alias: "au", label: "attendee_user", x: 600, y: 360, columns: [
    { name: "attendee_email" },
    { name: "user_external_id" },
    { name: "signup_date_utc" },
    { name: "user_country_code" },
    { name: "company_external_id" },
  ] },

  // Outcome
  { id: "company_billing", schema: "public", alias: "cb", label: "company_billing", x: 1100, y: 880, columns: [
    { name: "company_external_id" },
    { name: "billing_plan", note: "plus / pro" },
    { name: "subscription_started_date_utc" },
  ] },
  { id: "paid_cte", schema: "cte", alias: "p", label: "paid", x: 1100, y: 600, columns: [
    { name: "company_external_id" },
    { name: "on_paid_plan", note: "bool_or" },
    { name: "billing_plan / subscription_started_date_utc" },
  ] },

  // Activation
  { id: "synopsis", schema: "public", alias: "ms", label: "meeting_synopsis", x: 280, y: 880, columns: [
    { name: "user_external_id" },
    { name: "meeting_external_id", note: "→ meeting" },
    { name: "created_date_utc" },
  ] },
  { id: "meeting", schema: "public", alias: "m", label: "meeting", x: 280, y: 1140, columns: [
    { name: "external_id" },
    { name: "is_demo", note: "= false (excludes demo content)" },
    { name: "deleted_date_utc", note: "IS NULL" },
  ] },
  { id: "activated_cte", schema: "cte", alias: "act", label: "activated", x: 280, y: 600, columns: [
    { name: "user_external_id" },
    { name: "first_synopsis_date" },
  ] },
];

export const DEMO_FUNNEL_JOINS: JoinSpec[] = [
  // Spine + CTE chain
  { id: "demo-demos", source: "demo", target: "demos_cte", kind: "cte", label: "is_demo=true" },
  { id: "demos-da", source: "demos_cte", target: "demo_att_cte", kind: "cte", label: "unnest attendees" },
  { id: "da-fd", source: "demo_att_cte", target: "first_demo_cte", kind: "cte", label: "MIN(date)" },

  // Booking match
  { id: "demos-bm", source: "demos_cte", target: "booking_match_cte", kind: "cte", label: "±60d closest" },
  { id: "lc-bm", source: "lead_cal", target: "booking_match_cte", kind: "email", label: "client_email ∈ attendee_emails" },

  // Attendee → user
  { id: "da-au", source: "demo_att_cte", target: "attendee_user_cte", kind: "email", label: "lower(email)" },
  { id: "u-au", source: "users", target: "attendee_user_cte", kind: "direct", label: "lower(email)" },
  { id: "ur-au", source: "user_roles", target: "attendee_user_cte", kind: "direct", label: "user_external_id" },
  { id: "ur-c", source: "user_roles", target: "companies", kind: "direct", label: "company_external_id" },

  // Paid
  { id: "cb-p", source: "company_billing", target: "paid_cte", kind: "cte", label: "bool_or paid" },
  { id: "au-p", source: "attendee_user_cte", target: "paid_cte", kind: "direct", label: "company_external_id" },

  // Activation
  { id: "ms-m", source: "synopsis", target: "meeting", kind: "direct", label: "meeting_external_id" },
  { id: "ms-act", source: "synopsis", target: "activated_cte", kind: "cte", label: "MIN(date)" },
  { id: "au-act", source: "attendee_user_cte", target: "activated_cte", kind: "direct", label: "user_external_id" },
];
